import { createContext, useCallback, useContext, useSyncExternalStore } from 'react';
import type { StepStatus } from './VerticalStepForm.model';
import type { StepFormEngine } from './VerticalStepFormEngine';

interface StepFormContextValue {
  engine: StepFormEngine;
}

const StepFormContext = createContext<StepFormContextValue | null>(null);

export const StepFormProvider = StepFormContext.Provider;

export const useStepFormEngine = (): StepFormEngine => {
  const ctx = useContext(StepFormContext);
  if (ctx === null) {
    throw new Error('useStepFormEngine must be used within a VerticalStepForm');
  }
  return ctx.engine;
};

export const useStepStatus = (stepId: string): StepStatus => {
  const engine = useStepFormEngine();
  const subscribe = useCallback(
    (listener: () => void) => engine.subscribe(`step:${stepId}`, listener),
    [engine, stepId]
  );
  return useSyncExternalStore(subscribe, () => engine.getStepStatus(stepId));
};

export const useActiveStepId = (): string | null => {
  const engine = useStepFormEngine();
  const subscribe = useCallback((listener: () => void) => engine.subscribe('activeStep', listener), [engine]);
  return useSyncExternalStore(subscribe, () => engine.getActiveStepId());
};

export const useCanSubmit = (): boolean => {
  const engine = useStepFormEngine();
  const subscribe = useCallback((listener: () => void) => engine.subscribe('canSubmit', listener), [engine]);
  return useSyncExternalStore(subscribe, () => engine.canSubmit());
};

export interface StepCounts {
  completed: number;
  skipped: number;
}

export const useStepCounts = (): StepCounts => {
  const engine = useStepFormEngine();
  const subscribe = useCallback((listener: () => void) => engine.subscribe('counts', listener), [engine]);
  const completed = useSyncExternalStore(subscribe, () => engine.getCompletedCount());
  const skipped = useSyncExternalStore(subscribe, () => engine.getSkippedCount());
  return { completed, skipped };
};
